import React, { Children, lazy } from 'react';
import { motion } from 'framer-motion';
import { LeafIcon, UsersIcon, BriefcaseIcon, LineChartIcon, ShieldIcon, CheckIcon, SparklesIcon, ArrowUpRightIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
/* ----------------------------- HERO ----------------------------- */
const heroImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/1yGaZs6UDc71LTJ4nUnW8W/Sunday_Ojo,_Sustainability_-_ESG_professional.jpg',
  // top-left
  position: 'top-[12%] left-[3%]',
  size: 'w-32 h-40 md:w-44 md:h-56',
  rotate: '-rotate-[6deg]',
  delay: 0.2
}, {
  src: 'https://cdn.magicpatterns.com/uploads/7YRFsd6ct2rE1NgT1NUeK1/Andrew_Igbineweka,_Founder_.jpg',
  // top-right
  position: 'top-[8%] right-[5%]',
  size: 'w-36 h-44 md:w-48 md:h-60',
  rotate: 'rotate-[5deg]',
  delay: 0.5
}, {
  src: 'https://cdn.magicpatterns.com/uploads/sKqL918qPCSdd1CQUYev2X/Bella_Ojoka,_Sustainability_Professional.jpg',
  // mid-left
  position: 'top-[55%] left-[6%]',
  size: 'w-28 h-36 md:w-40 md:h-52',
  rotate: 'rotate-[4deg]',
  delay: 0.8
}, {
  src: 'https://cdn.magicpatterns.com/uploads/oFZTffcYGMd2xq5dg6yXvP/Ozioma_Chinedu,_Sustainability_Professional.jpg',
  // mid-right
  position: 'top-[50%] right-[4%]',
  size: 'w-32 h-40 md:w-44 md:h-56',
  rotate: '-rotate-[5deg]',
  delay: 1.1
}, {
  src: 'https://cdn.magicpatterns.com/uploads/7RuYi4yZQNm8RvznxtoJCn/Caleb_Joshua,_Environmental_Analyst,_NNPC_Energy_Services_Ltd..jpg',
  // bottom-center (slightly left)
  position: 'bottom-[6%] left-[40%] md:left-[44%]',
  size: 'w-28 h-36 md:w-40 md:h-52',
  rotate: '-rotate-[3deg]',
  delay: 1.4
}];
export function Hero() {
  return <section className="relative pt-32 pb-24 md:pt-48 md:pb-40 px-6 overflow-hidden min-h-[90vh] flex items-center" data-id="element-85">
      {/* Base background */}
      <div className="absolute inset-0 bg-cream z-0" data-id="element-86" />
      <div className="absolute top-0 right-0 w-1/2 h-full bg-sage/5 rounded-bl-full z-0" data-id="element-87" />

      {/* Animated background image blocks */}
      <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden="true" data-id="element-88">
        {heroImages.map((img, i) => <motion.div key={i} initial={{
        opacity: 0,
        scale: 0.6,
        y: 30
      }} animate={{
        opacity: 0.35,
        scale: 1,
        y: 0
      }} transition={{
        delay: img.delay,
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className={`absolute ${img.position} ${img.size} ${img.rotate} hidden sm:block`} data-id="element-89">
            <div className="relative w-full h-full overflow-hidden shadow-xl ring-1 ring-forest/10" data-id="element-90">
              <img src={img.src} alt="" className="w-full h-full object-cover grayscale-[20%]" loading="lazy" decoding="async" data-id="element-91" />
              {/* Slight tint to keep them subtle */}
              <div className="absolute inset-0 bg-forest/10 mix-blend-multiply" data-id="element-92" />
            </div>
          </motion.div>)}
        {/* Cream wash so text remains legible */}
        <div className="absolute inset-0 bg-gradient-to-b from-cream/40 via-cream/60 to-cream/80 md:from-cream/30 md:via-cream/40 md:to-cream/70" data-id="element-93" />
      </div>

      {/* Hero content */}
      <div className="max-w-5xl mx-auto relative z-10 text-center w-full" data-id="element-94">
        <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-95">
          <p className="text-sage font-medium tracking-[0.2em] uppercase text-sm mb-6" data-id="element-96">
            The Networking Salon
          </p>
          <h1 className="font-serif text-5xl md:text-7xl text-forest leading-tight mb-8" data-id="element-97">
            For Sustainability <br className="hidden md:block" data-id="element-98" /> Professionals
          </h1>
          <p className="text-xl md:text-2xl text-charcoal-light max-w-2xl mx-auto mb-12 font-light" data-id="element-99">
            Join the Salon. Lead the movement.
          </p>
          <button onClick={() => document.getElementById('memberships')?.scrollIntoView({
          behavior: 'smooth'
        })} className="bg-forest text-cream px-8 py-4 text-lg hover:bg-forest-light transition-colors duration-300 inline-flex items-center gap-2" data-id="element-100">
            View Memberships
          </button>
        </motion.div>
      </div>
    </section>;
}
/* -------------------------- FACILITATORS -------------------------- */
const facilitators = [{
  name: 'Sunday Ojo',
  role: 'Sustainability & ESG Professional',
  image: 'https://cdn.magicpatterns.com/uploads/1yGaZs6UDc71LTJ4nUnW8W/Sunday_Ojo,_Sustainability_-_ESG_professional.jpg'
}, {
  name: 'Andrew Igbineweka',
  role: 'Founder',
  image: 'https://cdn.magicpatterns.com/uploads/7YRFsd6ct2rE1NgT1NUeK1/Andrew_Igbineweka,_Founder_.jpg'
}, {
  name: 'Bella Ojoka',
  role: 'Sustainability Professional',
  image: 'https://cdn.magicpatterns.com/uploads/sKqL918qPCSdd1CQUYev2X/Bella_Ojoka,_Sustainability_Professional.jpg'
}, {
  name: 'Ozioma Chinedu',
  role: 'Sustainability Professional',
  image: 'https://cdn.magicpatterns.com/uploads/oFZTffcYGMd2xq5dg6yXvP/Ozioma_Chinedu,_Sustainability_Professional.jpg'
}, {
  name: 'Caleb Joshua',
  role: 'Environmental Analyst, NNPC Energy Services Ltd.',
  image: 'https://cdn.magicpatterns.com/uploads/7RuYi4yZQNm8RvznxtoJCn/Caleb_Joshua,_Environmental_Analyst,_NNPC_Energy_Services_Ltd..jpg'
}];
export function Facilitators() {
  return <section id="facilitators" className="py-24 px-6 bg-cream" data-id="element-101">
      <div className="max-w-7xl mx-auto" data-id="element-102">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="text-center mb-16" data-id="element-103">
          <p className="text-sage font-medium tracking-[0.2em] uppercase text-sm mb-4" data-id="element-104">
            The People
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-forest mb-4" data-id="element-105">
            Meet Some of the Facilitators
          </h2>
          <p className="text-charcoal-light max-w-2xl mx-auto text-lg" data-id="element-106">
            Practitioners who have done the work — guiding our members with
            insight, experience, and access.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={{
        hidden: {
          opacity: 0
        },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.18
          }
        }
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-107">
          {facilitators.map(f => <motion.article key={f.name} variants={{
          hidden: {
            opacity: 0,
            y: 40,
            scale: 0.95
          },
          visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
              duration: 0.6,
              ease: [0.16, 1, 0.3, 1]
            }
          }
        }} className="group bg-white border border-sage/15 hover:border-forest transition-all duration-300 flex flex-col overflow-hidden hover:shadow-xl hover:shadow-forest/5" data-id="element-108">
              <div className="relative aspect-[4/5] overflow-hidden bg-cream-dark" data-id="element-109">
                <img src={f.image} alt={f.name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" width={400} height={500} data-id="element-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-111" />
              </div>

              <div className="p-5 flex flex-col flex-grow" data-id="element-112">
                <h3 className="font-serif text-xl text-forest mb-1 leading-tight" data-id="element-113">
                  {f.name}
                </h3>
                <p className="text-xs text-charcoal-light mb-5 leading-relaxed flex-grow" data-id="element-114">
                  {f.role}
                </p>

                <a href="https://thenetworkingsalon.live/green" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-between gap-2 w-full text-sm font-medium text-forest border border-forest/30 py-2.5 px-3 hover:bg-forest hover:text-cream hover:border-forest transition-colors group/btn" data-id="element-115">
                  Click to view more
                  <ArrowUpRightIcon size={16} className="transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" data-id="element-116" />
                </a>
              </div>
            </motion.article>)}
        </motion.div>
      </div>
    </section>;
}
/* --------------------------- MISSION --------------------------- */
export function Mission() {
  return <section id="about" className="py-24 px-6 bg-forest text-cream" data-id="element-117">
      <div className="max-w-4xl mx-auto" data-id="element-118">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="text-center" data-id="element-119">
          <LeafIcon className="mx-auto text-gold mb-8" size={32} data-id="element-120" />
          <h2 className="font-serif text-3xl md:text-5xl mb-8 leading-snug" data-id="element-121">
            Bridging the gap between ambition and execution.
          </h2>
          <p className="text-lg md:text-xl text-sage-light leading-relaxed font-light" data-id="element-122">
            The Networking Salon is a curated, high-touch community designed to
            accelerate the careers and impact of sustainability leaders across
            industries. We connect professionals with the peers, mentors, and
            resources they need to drive real-world environmental and social
            change.
          </p>
        </motion.div>
      </div>
    </section>;
}
/* ------------------------- WHO WE SERVE ------------------------- */
export function WhoWeServe() {
  const audiences = ['Mid-to-senior sustainability professionals seeking career advancement', 'ESG analysts, CSO/CSR officers, impact investors, and climate strategists', 'Corporate sustainability leads navigating organizational transformation', 'Consultants and independent practitioners building their authority', 'Sustainability entrepreneurs and venture-backed climate tech founders'];
  return <section className="py-24 px-6 bg-cream" data-id="element-123">
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-16 items-center" data-id="element-124">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} data-id="element-125">
          <h2 className="font-serif text-4xl text-forest mb-6" data-id="element-126">Who We Serve</h2>
          <p className="text-charcoal-light text-lg mb-8" data-id="element-127">
            Our community is built for those actively shaping the future of
            business and environment.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="space-y-4" data-id="element-128">
          {audiences.map((item, i) => <motion.div key={i} variants={fadeIn} className="flex items-start gap-4 p-4 bg-white border border-sage/20" data-id="element-129">
              <CheckIcon className="text-gold shrink-0 mt-1" size={20} data-id="element-130" />
              <p className="text-charcoal" data-id="element-131">{item}</p>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
/* ------------------------ VALUE PILLARS ------------------------ */
export function ValuePillars() {
  const pillars = [{
    icon: <UsersIcon size={24} data-id="element-132" />,
    title: 'Curated Community',
    desc: 'Vetted members only — no noise, all signal'
  }, {
    icon: <SparklesIcon size={24} data-id="element-133" />,
    title: 'Expert Access',
    desc: 'Mentors who have done the work — not just theorists'
  }, {
    icon: <BriefcaseIcon size={24} data-id="element-134" />,
    title: 'Career Capital',
    desc: 'Resources, introductions, and visibility that move careers forward'
  }, {
    icon: <LineChartIcon size={24} data-id="element-135" />,
    title: 'Industry Intelligence',
    desc: 'Timely insights on ESG regulation, funding, and market shifts'
  }, {
    icon: <ShieldIcon size={24} data-id="element-136" />,
    title: 'Trusted Network',
    desc: 'Relationships with peers across sectors, regions, and disciplines'
  }];
  return <section className="py-24 px-6 bg-cream-dark/30 border-y border-sage/20" data-id="element-137">
      <div className="max-w-7xl mx-auto" data-id="element-138">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="text-center mb-16" data-id="element-139">
          <h2 className="font-serif text-4xl text-forest mb-4" data-id="element-140">
            Our Value Proposition
          </h2>
          <p className="text-charcoal-light max-w-2xl mx-auto" data-id="element-141">
            Sustainability professionals often operate in isolation. We provide
            the peer support, intelligence, and access needed to thrive.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-id="element-142">
          {pillars.map((pillar, i) => <motion.div key={i} variants={fadeIn} className="bg-white p-6 border border-sage/20 hover:border-gold transition-colors" data-id="element-143">
              <div className="w-12 h-12 bg-forest/5 text-forest flex items-center justify-center mb-6" data-id="element-144">
                {pillar.icon}
              </div>
              <h3 className="font-serif text-xl text-forest mb-3" data-id="element-145">
                {pillar.title}
              </h3>
              <p className="text-sm text-charcoal-light leading-relaxed" data-id="element-146">
                {pillar.desc}
              </p>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
/* ----------------------------- QUOTE ----------------------------- */
export function Quote() {
  return <section className="py-32 px-6 bg-forest text-cream text-center" data-id="element-147">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeIn} className="max-w-4xl mx-auto" data-id="element-148">
        <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-8 text-gold" data-id="element-149">
          "The future of sustainability is shaped by the people we build."
        </h2>
        <p className="text-sage-light tracking-widest uppercase text-sm" data-id="element-150">
          The Networking Salon
        </p>
      </motion.div>
    </section>;
}
/* ----------------------------- FOOTER ----------------------------- */
export function Footer() {
  return <footer className="bg-charcoal text-cream py-12 px-6 text-center" data-id="element-151">
      <p className="font-serif text-xl mb-4 text-gold" data-id="element-152">The Networking Salon</p>
      <p className="text-sm text-charcoal-light mb-8" data-id="element-153">
        For Sustainability Professionals
      </p>
      <div className="text-xs text-charcoal-light pt-8 border-t border-charcoal-light/20" data-id="element-154">
        &copy; {new Date().getFullYear()} The Networking Salon. All rights
        reserved.
      </div>
    </footer>;
}